import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { interviewModeLabel, interviewStatusLabel } from "@/lib/constants";
import { CalendarCheck } from "lucide-react";

export default function CandidateInterviews() {
  const [interviews, setInterviews] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/interviews/mine");
      setInterviews(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const respond = async (interviewId, response) => {
    setError("");
    try {
      await api.post(`/interviews/${interviewId}/respond`, { response });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div data-testid="candidate-interviews">
      <h2 className="font-semibold text-brand-navy mb-4 flex items-center gap-2">
        <CalendarCheck className="w-4 h-4 text-brand-turquoise" />
        Mes entretiens ({interviews.length})
      </h2>
      {error && (
        <div data-testid="interviews-error" className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</div>
      )}
      <div className="space-y-3">
        {interviews.map((i) => (
          <div key={i.interview_id} data-testid={`interview-card-${i.interview_id}`} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-brand-navy text-sm truncate">{i.offer_title}</p>
                <p className="text-xs text-slate-500">{i.company}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {i.scheduled_at ? new Date(i.scheduled_at).toLocaleString("fr-CA") : "—"} · {interviewModeLabel(i.mode)}
                  {i.location_or_link ? ` · ${i.location_or_link}` : ""}
                </p>
              </div>
              <span
                data-testid={`interview-status-${i.interview_id}`}
                className={`text-xs font-medium rounded-full px-3 py-1 shrink-0 ${
                  i.status === "annule" ? "bg-red-50 text-red-600" : i.status === "realise" ? "bg-green-50 text-green-700" : "bg-brand-sky text-brand-navy"
                }`}
              >
                {interviewStatusLabel(i.status)}
                {i.candidate_response === "accepte" ? " · Accepté" : i.candidate_response === "decline" ? " · Décliné" : ""}
              </span>
            </div>
            {i.status === "planifie" && !i.candidate_response && (
              <div className="flex gap-2 mt-3">
                <button
                  data-testid={`interview-accept-${i.interview_id}`}
                  onClick={() => respond(i.interview_id, "accepte")}
                  className="text-xs font-medium text-green-700 border border-green-300 rounded-lg px-3 py-1.5 hover:bg-green-50"
                >
                  Accepter
                </button>
                <button
                  data-testid={`interview-decline-${i.interview_id}`}
                  onClick={() => respond(i.interview_id, "decline")}
                  className="text-xs font-medium text-red-600 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50"
                >
                  Décliner
                </button>
              </div>
            )}
          </div>
        ))}
        {interviews.length === 0 && (
          <p data-testid="interviews-empty" className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-6 text-center">
            Aucun entretien planifié pour le moment.
          </p>
        )}
      </div>
    </div>
  );
}
